// =====================================================
// GVC CELEBRATIONS SERVICE - Frontend API Client
// Handles: Google Reviews Contest + Birthday Celebrations
// =====================================================

import API_URL, { authFetch } from '../config/api';

// P1-B: the bearer token is attached by authFetch (session module); no storage reads here.
const getAuthHeaders = () => ({
  'Content-Type': 'application/json'
});

const celebrationsService = {
  // =====================================================
  // CONTESTS
  // =====================================================
  
  getContests: async () => {
    const response = await authFetch(`${API_URL}/api/celebrations/contests`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch contests');
    return response.json();
  },

  getActiveContest: async () => {
    const response = await authFetch(`${API_URL}/api/celebrations/contests/active`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch active contest');
    return response.json();
  },

  createContest: async (contestData) => {
    const response = await authFetch(`${API_URL}/api/celebrations/contests`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(contestData)
    });
    if (!response.ok) throw new Error('Failed to create contest');
    return response.json();
  },

  updateContest: async (id, contestData) => {
    const response = await authFetch(`${API_URL}/api/celebrations/contests/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(contestData)
    });
    if (!response.ok) throw new Error('Failed to update contest');
    return response.json();
  },

  // =====================================================
  // PARTICIPANTS
  // =====================================================

  getParticipants: async (contestId = null) => {
    const url = contestId 
      ? `${API_URL}/api/celebrations/participants?contest_id=${contestId}`
      : `${API_URL}/api/celebrations/participants`;
    const response = await authFetch(url, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch participants');
    return response.json();
  },

  registerParticipant: async (participantData) => {
    const response = await authFetch(`${API_URL}/api/celebrations/participants/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(participantData)
    });
    if (!response.ok) throw new Error('Failed to register participant');
    return response.json();
  },

  lookupParticipant: async (phone) => {
    const response = await authFetch(`${API_URL}/api/celebrations/participants/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone })
    });
    if (!response.ok) throw new Error('Phone number not registered');
    return response.json();
  },

  updateParticipant: async (id, participantData) => {
    const response = await authFetch(`${API_URL}/api/celebrations/participants/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(participantData)
    });
    if (!response.ok) throw new Error('Failed to update participant');
    return response.json();
  },

  // =====================================================
  // CLICKS & TRACKING
  // =====================================================

  trackClick: async (slug, reviewerName = null) => {
    const response = await authFetch(`${API_URL}/api/celebrations/clicks/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug, reviewer_name: reviewerName })
    });
    if (!response.ok) throw new Error('Failed to track click');
    return response.json();
  },

  getClicks: async (participantId) => {
    const response = await authFetch(`${API_URL}/api/celebrations/clicks/${participantId}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch clicks');
    return response.json();
  },

  // =====================================================
  // REVIEWS
  // =====================================================

  getReviews: async (status = null) => {
    const url = status 
      ? `${API_URL}/api/celebrations/reviews?status=${status}`
      : `${API_URL}/api/celebrations/reviews`;
    const response = await authFetch(url, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch reviews');
    return response.json();
  },

  addReview: async (reviewData) => {
    const response = await authFetch(`${API_URL}/api/celebrations/reviews`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(reviewData)
    });
    if (!response.ok) throw new Error('Failed to add review');
    return response.json();
  },

  attributeReview: async (reviewId, attributionData) => {
    const response = await authFetch(`${API_URL}/api/celebrations/reviews/${reviewId}/attribute`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(attributionData)
    });
    if (!response.ok) throw new Error('Failed to attribute review');
    return response.json();
  },

  // =====================================================
  // LEADERBOARD
  // =====================================================

  getLeaderboard: async (contestId = null, limit = null) => {
    let url = `${API_URL}/api/celebrations/leaderboard`;
    const params = new URLSearchParams();
    if (contestId) params.append('contest_id', contestId);
    if (limit) params.append('limit', limit);
    if (params.toString()) url += `?${params.toString()}`;
    
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch leaderboard');
    return response.json();
  },

  // =====================================================
  // BIRTHDAYS
  // =====================================================

  getBirthdays: async () => {
    const response = await authFetch(`${API_URL}/api/celebrations/birthdays`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch birthdays');
    return response.json();
  },

  getActiveBirthdays: async () => {
    const response = await authFetch(`${API_URL}/api/celebrations/birthdays/active`);
    if (!response.ok) throw new Error('Failed to fetch active birthdays');
    return response.json();
  },

  getTodayBirthdays: async () => {
    const response = await authFetch(`${API_URL}/api/celebrations/birthdays/today`);
    if (!response.ok) throw new Error('Failed to fetch today birthdays');
    return response.json();
  },

  createBirthday: async (birthdayData) => {
    const response = await authFetch(`${API_URL}/api/celebrations/birthdays`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(birthdayData)
    });
    if (!response.ok) throw new Error('Failed to create birthday');
    return response.json();
  },

  updateBirthday: async (id, birthdayData) => {
    const response = await authFetch(`${API_URL}/api/celebrations/birthdays/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(birthdayData)
    });
    if (!response.ok) throw new Error('Failed to update birthday');
    return response.json();
  },

  deleteBirthday: async (id) => {
    const response = await authFetch(`${API_URL}/api/celebrations/birthdays/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to delete birthday');
    return response.json();
  },

  // =====================================================
  // DISPLAY (Public - for Raspberry Pi)
  // =====================================================

  getDisplay: async () => {
    const response = await authFetch(`${API_URL}/api/celebrations/display`);
    if (!response.ok) throw new Error('Failed to fetch display data');
    return response.json();
  },

  // =====================================================
  // SETTINGS
  // =====================================================

  getSettings: async () => {
    const response = await authFetch(`${API_URL}/api/celebrations/settings`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch settings');
    return response.json();
  },

  updateSetting: async (settingKey, settingValue) => {
    const response = await authFetch(`${API_URL}/api/celebrations/settings`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ setting_key: settingKey, setting_value: settingValue })
    });
    if (!response.ok) throw new Error('Failed to update setting');
    return response.json();
  },

  // =====================================================
  // STATS
  // =====================================================

  getStats: async () => {
    const response = await authFetch(`${API_URL}/api/celebrations/stats`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch stats');
    return response.json();
  }
};

export { celebrationsService };
